import { type ReactNode, type RefObject, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConfigProvider } from 'antd';
import { useSnapshot } from 'valtio';
import type { SplitQuery, VisibilityTypeId } from '#types/System.types';
import { formStore } from '../../../store';
import FormUI from '../../rendering/FormUI';
import LayoutUI from '../../rendering/LayoutUI';
import PdfExportProvider from '../context/PdfExportProvider';
import { exportThemeConfig } from '../theme/exportThemeConfig';
import {
  PDF_COLOR_BACKGROUND,
  PDF_EXPORT_CONTAINER_PADDING,
  PDF_EXPORT_CONTAINER_WIDTH,
  PDF_EXPORT_STYLE_OVERRIDES,
} from '#types/System.Themes.types';

interface UsePdfPortalParams {
  open: boolean;
  sectionStructure?: unknown;
  /**
   * Visibility view the portal content renders at (SYD-1948). Provided to the
   * subtree via `PdfExportProvider`; leave undefined to render at the live
   * on-screen view.
   */
  visibilityMode?: VisibilityTypeId;
  /**
   * Row scope the portal content renders at (SYD-2302). Provided to the subtree
   * via `PdfExportProvider`, which `useArTableSplit` prefers over the on-screen
   * `formStore` split; leave undefined to render the whole document.
   */
  splitQuery?: SplitQuery;
}

interface UsePdfPortalReturn {
  portalNode: ReactNode;
  contentRef: RefObject<HTMLDivElement | null>;
  ready: boolean;
}

/**
 * Manages a hidden iframe portal that renders form content with export theme.
 * Provides a stable contentRef for PDF generation via usePdfExport.
 *
 * The portal owns no scope of its own: `visibilityMode` and `splitQuery` are
 * forwarded straight to `PdfExportProvider`, so the caller decides when a new
 * scope has committed to the iframe DOM before serializing it.
 *
 * It does own the decision of when there is anything to capture (SYD-2441).
 * Every consumer gates its capture on `ready`, and `open` alone let the
 * container mount while `FormUI` still served a Skeleton — so a `formType` that
 * had not resolved yet produced a PDF of the loading placeholder.
 */
export const usePdfPortal = ({ open, sectionStructure, visibilityMode, splitQuery }: UsePdfPortalParams): UsePdfPortalReturn => {
  const snap = useSnapshot(formStore.state);
  const formUiHasContent = !snap.formType.loading
    && Boolean(snap.formType.data?.defaultUILayout?.structure);
  /**
   * Mirrors `FormUI`'s own render gate, which is what the portal captures. A
   * caller-supplied `sectionStructure` renders `LayoutUI` instead and carries
   * its content with it, so it needs no store read.
   */
  const active = open && (Boolean(sectionStructure) || formUiHasContent);

  const contentRef = useRef<HTMLDivElement>(null);
  const pendingIframeRef = useRef<HTMLIFrameElement | null>(null);
  const pendingLoadHandlerRef = useRef<EventListener | null>(null);
  const [contentReady, setContentReady] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const iframeRefCallback = useCallback((node: HTMLIFrameElement | null) => {
    if (pendingIframeRef.current && pendingLoadHandlerRef.current) {
      pendingIframeRef.current.removeEventListener('load', pendingLoadHandlerRef.current);
    }
    pendingIframeRef.current = null;
    pendingLoadHandlerRef.current = null;

    if (!node) {
      setPortalTarget(null);
      setContentReady(false);
      return;
    }
    const body = node.contentDocument?.body;
    if (body) {
      setPortalTarget(body);
      return;
    }

    const handleLoad: EventListener = () => {
      if (pendingIframeRef.current !== node) {
        return;
      }
      setPortalTarget(node.contentDocument?.body ?? null);
    };

    pendingIframeRef.current = node;
    pendingLoadHandlerRef.current = handleLoad;
    node.addEventListener('load', handleLoad, { once: true });
  }, []);

  /**
   * Sole owner of `contentRef` (SYD-2441). `ready` tracks the attachment rather
   * than latching on it, so a detached container reports not-ready instead of
   * letting a capture run against a null ref.
   */
  const contentRefCallback = useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    setContentReady(node !== null);
  }, []);

  const portalNode: ReactNode = active ? (
    <>
      <iframe
        ref={iframeRefCallback}
        style={{ display: 'none' }}
        title="PDF Export Container"
      />
      {portalTarget && createPortal(
        <div
          ref={contentRefCallback}
          style={{
            width: PDF_EXPORT_CONTAINER_WIDTH,
            backgroundColor: PDF_COLOR_BACKGROUND,
            padding: PDF_EXPORT_CONTAINER_PADDING,
          }}
        >
          <PdfExportProvider isPdfExportMode visibilityMode={visibilityMode} splitQuery={splitQuery}>
            <ConfigProvider theme={exportThemeConfig}>
              <style
                id="pdf-export-styles"
                dangerouslySetInnerHTML={{ __html: PDF_EXPORT_STYLE_OVERRIDES }}
              />
              {sectionStructure ? (
                <LayoutUI
                  uiElement={sectionStructure as any}
                  disabled
                />
              ) : (
                <FormUI forceDisabled />
              )}
            </ConfigProvider>
          </PdfExportProvider>
        </div>,
        portalTarget
      )}
    </>
  ) : null;

  return { portalNode, contentRef, ready: active && portalTarget !== null && contentReady };
};
